import { FetchEnvironmentsApi } from '@elementary/common-ui/types';
import {
  ByokSettings,
  DbtCloudConnection,
} from '@elementary/common-ui/services/api';
import { get, post, del, put, patch } from '@elementary/common-ui/utils/http';
import {
  UserInviteForm,
  FetchSlackApi,
  SetSlackApi,
  SendSlackTestMessageApi,
  SendSlackTestMessageResponseApi,
  SetWebhookApi,
  FetchTeamUsersApi,
  FetchNangoApi,
  FetchNangoInstalledAppsApi,
  UserUpdateForm,
  FetchPagerDutyApi,
  SetMicrosoftTeamsWebhooksApi,
  SetMicrosoftTeamsDefaultsApi,
  FetchMicrosoftTeamsWebhooksApi,
  SendTeamsTestMessageApi,
  SendTeamsTestMessageResponseApi,
  UpdateOpsgenieApi,
  FetchOpsgenieApi,
  SetOpsgenieApi,
  FetchIncidentIoApi,
  SetIncidentIoApi,
  UpdateIncidentIoApi,
  SendIncidentIoTestIncidentApi,
  SendIncidentIoTestIncidentResponseApi,
  FetchRootlyApi,
  SetRootlyApi,
  UpdateRootlyApi,
  SendRootlyTestIncidentApi,
  SendRootlyTestIncidentResponseApi,
  FetchMicrosoftTeamsDefaultsApi,
  FetchAccountSettingsApi,
  SetAccountSettingsApi,
  FetchTicketingApi,
  FetchTicketingProjectsApi,
  CreateTicketingApi,
  UpdateTicketingApi,
  TicketingTool,
  SetBiIntegrationApi,
  FetchBiIntegrationsApi,
  FetchSlackChannelsApi,
  CreateServiceNowConfigApi,
  ServiceNowConfigApi,
  FetchEnvironmentApi,
  SendOpsgenieTestIncidentApi,
  SendOpsgenieTestIncidentResponseApi,
  SendPagerDutyTestIncidentApi,
  SendPagerDutyTestIncidentResponseApi,
  FetchDataStoresApi,
  SetDataStoreApi,
  CreateEnvironmentApiRequest,
  UpdateEnvironmentApiRequest,
  CreateActivityLogsIntegrationApi,
  FetchActivityLogsIntegrationsApi,
  UpdateActivityLogsIntegrationApi,
  JiraWebhookConfig,
  GenerateJiraWebhookSecretResponse,
  EnvironmentIntegrations,
  FivetranIntegrationResponse,
  CreateLegacyFivetranIntegrationResponse,
  CreateFivetranIntegrationResponse,
  PatchFivetranIntegrationResponse,
  FivetranDestinationGroups,
  SetSlackIntegrationResponse,
  PreDeleteResponse,
} from 'shared/types';

export const fetchUsers = (): Promise<FetchTeamUsersApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/users/`);

export type SpendRange = '3m' | '6m' | '1y' | 'mtd' | 'ytd';

export type AiUsageSpendByDay = {
  day: string;
  agent: number;
  ci: number;
};

export type AiUsageLeaderboardEntry = {
  user_id: string;
  name: string | null;
  email: string | null;
  credits: number;
  ai_runs: number;
};

export type AiUsageSession = {
  thread_id: string;
  conversation_starter: string | null;
  user_id: string;
  name: string | null;
  email: string | null;
  started_at: string;
  credits: number;
};

export type PersonalAiUsageData = {
  daily_credits_used: number;
  daily_credit_limit: number;
  daily_resets_at: string;
};

export type AiUsageData = {
  credits_used: number;
  credit_limit: number | null;
  resets_at: string;
  spend_by_day: AiUsageSpendByDay[];
  leaderboard: AiUsageLeaderboardEntry[];
  sessions: AiUsageSession[];
};

export const fetchPersonalAiUsage = (): Promise<PersonalAiUsageData> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/ai-usage/personal`);

export const fetchAiUsage = (range: SpendRange): Promise<AiUsageData> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/ai-usage/`, { range });

export const updateUser = ({ userId, name, role_id }: UserUpdateForm) =>
  put(`${import.meta.env.VITE_API_BASE_URL}/account/users/${userId}`, {
    name,
    role_id,
  });

export const updateOwnUser = (fullName?: string) =>
  put(`${import.meta.env.VITE_API_BASE_URL}/account/users/me`, {
    name: fullName,
  });

export const changePasswordRequest = (
  currentPassword: string,
  newPassword: string,
) =>
  put(`${import.meta.env.VITE_API_BASE_URL}/account/users/me/password`, {
    current_password: currentPassword,
    new_password: newPassword,
  });

export const deleteUser = (id: string) =>
  del(`${import.meta.env.VITE_API_BASE_URL}/account/users/${id}`);

export const inviteUser = ({ name, email, role_id }: UserInviteForm) =>
  post(`${import.meta.env.VITE_API_BASE_URL}/account/users/`, {
    name,
    email,
    role_id,
  });

export const resendInvite = (id: string, email: string) =>
  post(`${import.meta.env.VITE_API_BASE_URL}/account/users/resend_invite`, {
    email,
  });

export const fetchPagerDuty = (): Promise<FetchPagerDutyApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/integrations/pager-duty/`);

export const setPagerDutyIntegration = () =>
  post(`${import.meta.env.VITE_API_BASE_URL}/account/integrations/pager-duty/`);

export const fetchOpsgenie = (): Promise<FetchOpsgenieApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/integrations/opsgenie/`);

export const setOpsgenieIntegration = (data: SetOpsgenieApi) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/account/integrations/opsgenie/`,
    data,
  );

export const updateOpsgenieIntegration = (data: UpdateOpsgenieApi) =>
  put(
    `${import.meta.env.VITE_API_BASE_URL}/account/integrations/opsgenie/`,
    data,
  );

export const fetchIncidentIo = (): Promise<FetchIncidentIoApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/integrations/incident-io/`);

export const setIncidentIoIntegration = (data: SetIncidentIoApi) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/account/integrations/incident-io/`,
    data,
  );

export const updateIncidentIoIntegration = (data: UpdateIncidentIoApi) =>
  put(
    `${import.meta.env.VITE_API_BASE_URL}/account/integrations/incident-io/`,
    data,
  );

export const sendIncidentIoTestIncident = (
  data: SendIncidentIoTestIncidentApi,
): Promise<SendIncidentIoTestIncidentResponseApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/incident-io/test-incident`,
    data,
  );

export const fetchRootly = (): Promise<FetchRootlyApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/integrations/rootly/`);

export const setRootlyIntegration = (data: SetRootlyApi) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/account/integrations/rootly/`,
    data,
  );

export const updateRootlyIntegration = (data: UpdateRootlyApi) =>
  put(
    `${import.meta.env.VITE_API_BASE_URL}/account/integrations/rootly/`,
    data,
  );

export const sendRootlyTestIncident = (
  data: SendRootlyTestIncidentApi,
): Promise<SendRootlyTestIncidentResponseApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/rootly/test-incident`,
    data,
  );

export const fetchAccountSettings = (): Promise<FetchAccountSettingsApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/account/settings`);

export const updateAccountSettings = (data: SetAccountSettingsApi) =>
  put(`${import.meta.env.VITE_API_BASE_URL}/account/settings`, data);

export type ByokProvider = 'anthropic' | 'openai';

export interface SetByokSettingsApi {
  provider?: ByokProvider;
  apiKey?: string;
  model?: string | null;
  allowedModels?: string[];
}

export const setByokSettings = (
  data: SetByokSettingsApi,
): Promise<ByokSettings> =>
  put(`${import.meta.env.VITE_API_BASE_URL}/account/settings/ai/byok`, data);

export const deleteByokSettings = (): Promise<ByokSettings> =>
  del(`${import.meta.env.VITE_API_BASE_URL}/account/settings/ai/byok`);

export interface CreateDbtCloudConnectionApi {
  name: string;
  hostUrl: string;
  serviceToken: string;
  dbtCloudProdEnvironmentId?: string | null;
}

export interface UpdateDbtCloudConnectionApi {
  name?: string;
  hostUrl?: string;
  serviceToken?: string;
  dbtCloudProdEnvironmentId?: string | null;
}

export const createDbtCloudConnection = (
  data: CreateDbtCloudConnectionApi,
): Promise<DbtCloudConnection> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/settings/integrations/dbt-cloud`,
    data,
  );

export const updateDbtCloudConnection = (
  connectionId: string,
  data: UpdateDbtCloudConnectionApi,
): Promise<DbtCloudConnection> =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/settings/integrations/dbt-cloud/${connectionId}`,
    data,
  );

export const deleteDbtCloudConnection = (connectionId: string): Promise<void> =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/settings/integrations/dbt-cloud/${connectionId}`,
  );

export const fetchEnvironments = (): Promise<FetchEnvironmentsApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/environments`);

export const fetchEnvironment = (envId: string): Promise<FetchEnvironmentApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/environments/${envId}`);

export const createEnvironment = (
  data: CreateEnvironmentApiRequest,
): Promise<void> =>
  post(`${import.meta.env.VITE_API_BASE_URL}/environments`, data);

export const updateEnvironment = (
  envId: string,
  data: UpdateEnvironmentApiRequest,
): Promise<void> =>
  put(`${import.meta.env.VITE_API_BASE_URL}/environments/${envId}`, data);

export const syncNow = (envId: string) =>
  post(`${import.meta.env.VITE_API_BASE_URL}/settings/sync_now/${envId}`);

export const fetchSlack = (envId: string): Promise<FetchSlackApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/settings/slack/${envId}`);

export const setSlack = (data: SetSlackApi, envId: string) =>
  post(`${import.meta.env.VITE_API_BASE_URL}/settings/slack/${envId}`, data);

export const updateSlack = (data: SetSlackApi, envId: string) =>
  put(`${import.meta.env.VITE_API_BASE_URL}/settings/slack/${envId}`, data);

export const setSlackIntegration = (): Promise<SetSlackIntegrationResponse> =>
  post(`${import.meta.env.VITE_API_BASE_URL}/settings/slack-integration`);

export const fetchMicrosoftTeamsWebhooks =
  (): Promise<FetchMicrosoftTeamsWebhooksApi> =>
    get(
      `${
        import.meta.env.VITE_API_BASE_URL
      }/account/integrations/microsoft-teams/`,
    );

export const fetchMicrosoftTeamsDefaults = (
  envId: string,
): Promise<FetchMicrosoftTeamsDefaultsApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/microsoft-teams`);

export const setMicrosoftTeamsWebhooks = (data: SetMicrosoftTeamsWebhooksApi) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/microsoft-teams/`,
    data,
  );

export const updateMicrosoftTeamsWebhooks = (
  data: SetMicrosoftTeamsWebhooksApi,
) =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/microsoft-teams/`,
    data,
  );

export const setMicrosoftTeamsDefaults = (
  data: SetMicrosoftTeamsDefaultsApi,
  envId: string,
) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/microsoft-teams`,
    data,
  );

export const updateMicrosoftTeamsDefaults = (
  data: SetMicrosoftTeamsDefaultsApi,
  envId: string,
) =>
  put(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/microsoft-teams`,
    data,
  );

export const sendSlackTestMessage = (
  data: SendSlackTestMessageApi,
): Promise<SendSlackTestMessageResponseApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/slack/test-message`,
    data,
  );

export const sendTeamsTestMessage = (
  data: SendTeamsTestMessageApi,
): Promise<SendTeamsTestMessageResponseApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/microsoft-teams/test-message`,
    data,
  );

export const fetchTeamsBotSettings = (): Promise<{
  is_connected: boolean;
  connected_workspaces: { tenant_id: string; team_id: string }[];
  manifest_url: string;
  app_id: string;
}> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/microsoft-teams-bot/`,
  );

export const linkTeamsBotInstallation = (
  token: string,
): Promise<{ success: boolean; message: string }> =>
  post(`${import.meta.env.VITE_API_BASE_URL}/app-integrations/teams/link`, {
    token,
  });

export const sendTeamsBotTestMessage = (
  channel: string,
  message?: string,
): Promise<{ success: boolean; message: string }> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/microsoft-teams-bot/test-message`,
    { channel, message: message || undefined },
  );

export const fetchTeamsBotDefaults = (
  envId: string,
): Promise<{ defaultDestination: { channel: string } | null }> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/microsoft-teams-bot`,
  );

export const setTeamsBotDefaults = (channel: string, envId: string) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/microsoft-teams-bot`,
    { channel },
  );

export const updateTeamsBotDefaults = (channel: string, envId: string) =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/microsoft-teams-bot`,
    { channel },
  );

export const sendOpsgenieTestIncident = (
  data: SendOpsgenieTestIncidentApi,
): Promise<SendOpsgenieTestIncidentResponseApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/opsgenie/test-incident`,
    data,
  );

export const sendPagerDutyTestIncident = (
  data: SendPagerDutyTestIncidentApi,
): Promise<SendPagerDutyTestIncidentResponseApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/pager-duty/test-incident`,
    data,
  );

export const fetchBiIntegrations = (
  envId: string,
): Promise<FetchBiIntegrationsApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/bi/`);

export const setBiIntegration = ({
  data,
  envId,
}: {
  data: SetBiIntegrationApi;
  envId: string;
}) => post(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/bi/`, data);

export const updateBiIntegration = ({
  data,
  id,
  envId,
}: {
  data: SetBiIntegrationApi;
  id: string;
  envId: string;
}) =>
  put(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/bi/${id}`, data);

export const deleteBiIntegration = ({
  id,
  envId,
}: {
  id: string;
  envId: string;
}) => del(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/bi/${id}`);

export const syncNowBiIntegration = ({
  id,
  envId,
}: {
  id: string;
  envId: string;
}) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/bi/${id}/refresh`,
  );

export const fetchTicketing = (envId: string): Promise<FetchTicketingApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/ticketing`);

export const fetchTicketingProjects = (
  envId: string,
  type: TicketingTool,
): Promise<FetchTicketingProjectsApi> =>
  get(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/ticketing/projects`,
    {
      type,
    },
  );

export const createServiceNowConfig = (
  envId: string,
  data: CreateServiceNowConfigApi,
): Promise<ServiceNowConfigApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/service-now/config`,
    data,
  );

export const fetchServiceNowConfig = (
  envId: string,
): Promise<ServiceNowConfigApi> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/service-now/config`,
  );

export const createTicketing = (data: CreateTicketingApi, envId: string) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/ticketing`,
    data,
  );

export const updateTicketing = (
  id: string,
  data: UpdateTicketingApi,
  envId: string,
) =>
  put(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/ticketing/${id}`,
    data,
  );

export const deleteTicketing = ({ id, envId }: { id: string; envId: string }) =>
  del(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/ticketing/${id}`);

export const fetchJiraSiteUrl = (
  envId: string,
  integrationId: string,
): Promise<{ base_url: string | null }> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/${integrationId}/jira/site-url`,
  );

export type TicketSearchResult = {
  key: string;
  summary: string;
  url: string;
};

export const searchTicketingIssues = (
  envId: string,
  integrationId: string,
  query: string,
): Promise<{ results: TicketSearchResult[] }> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/integrations/${integrationId}/issues/search`,
    { query },
  );

export const getTicketingIssue = (
  envId: string,
  integrationId: string,
  key: string,
): Promise<{ result: TicketSearchResult | null }> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/integrations/${integrationId}/issues/${encodeURIComponent(
      key,
    )}`,
  );

export const createStandaloneTicket = (
  envId: string,
  integrationId: string,
  customFields?: Record<string, any>,
): Promise<{ ticket: TicketSearchResult }> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/integrations/${integrationId}/tickets`,
    { custom_fields: customFields },
  );

export const fetchJiraWebhookConfig = (
  envId: string,
  integrationId: string,
): Promise<JiraWebhookConfig> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/${integrationId}/jira/webhook`,
  );

export const generateJiraWebhookSecret = (
  envId: string,
  integrationId: string,
): Promise<GenerateJiraWebhookSecretResponse> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/${envId}/ticketing/${integrationId}/jira/webhook/secret`,
    {},
  );

export const fetchNango = (
  provider: string,
  connectionId?: string,
): Promise<FetchNangoApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/settings/nango/link/${provider}`, {
    connection_id: connectionId,
  });

export const fetchNangoInstalledApps =
  (): Promise<FetchNangoInstalledAppsApi> =>
    get(`${import.meta.env.VITE_API_BASE_URL}/settings/nango/installed_apps`);

export const fetchSlackChannels = (
  envId: string,
): Promise<FetchSlackChannelsApi> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/settings/slack-list-channels/${envId}`,
    {},
  );

export const setWebhook = (data: SetWebhookApi, envId: string) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/environments/${envId}/sync-webhook`,
    data,
  );

export const fetchDataCatalogs = (envId: string): Promise<FetchDataStoresApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/metastore/`);

export const updateDataCatalog = ({
  data,
  id,
  envId,
}: {
  data: SetDataStoreApi;
  id: string;
  envId: string;
}) =>
  put(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/metastore/${id}`,
    data,
  );

export const setDataCatalog = ({
  data,
  envId,
}: {
  data: SetDataStoreApi;
  envId: string;
}) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/metastore/`,
    data,
  );

export const deleteDataCatalog = ({
  id,
  envId,
}: {
  id: string;
  envId: string;
}) =>
  del(`${import.meta.env.VITE_API_BASE_URL}/settings/${envId}/metastore/${id}`);

export const fetchActivityLogsIntegrations =
  (): Promise<FetchActivityLogsIntegrationsApi> =>
    get(`${import.meta.env.VITE_API_BASE_URL}/settings/activity-logs`);

export const createActivityLogsIntegration = (
  data: CreateActivityLogsIntegrationApi,
) => post(`${import.meta.env.VITE_API_BASE_URL}/settings/activity-logs`, data);

export const updateActivityLogsIntegration = ({
  id,
  data,
}: {
  id: string;
  data: UpdateActivityLogsIntegrationApi;
}) =>
  put(
    `${import.meta.env.VITE_API_BASE_URL}/settings/activity-logs/${id}`,
    data,
  );

export const deleteActivityLogsIntegration = (id: string) =>
  del(`${import.meta.env.VITE_API_BASE_URL}/settings/activity-logs/${id}`);

export const deleteEnvironment = (envId: string): Promise<void> =>
  del(`${import.meta.env.VITE_API_BASE_URL}/environments/${envId}`);

export const fetchEnvironmentIntegrations = (
  envId: string,
): Promise<EnvironmentIntegrations> =>
  get(
    `${import.meta.env.VITE_API_BASE_URL}/environments/${envId}/integrations`,
  );

export const fetchAllEnvironmentsIntegrations = (): Promise<
  Record<string, EnvironmentIntegrations>
> => get(`${import.meta.env.VITE_API_BASE_URL}/environments/integrations`);

export const fetchFivetranIntegration = (
  envId: string,
  integrationId: string,
): Promise<FivetranIntegrationResponse> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/etl-tool/fivetran/${integrationId}`,
  );

export const createFivetranIntegrationLegacy = (
  envId: string,
): Promise<CreateLegacyFivetranIntegrationResponse> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/etl-tool/fivetran/legacy`,
    {},
  );

export const listFivetranDestinationGroupsByKeySecret = (
  envId: string,
  apiKey: string,
  apiSecret: string,
): Promise<FivetranDestinationGroups> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/etl-tool/fivetran/list-destination-groups`,
    { api_key: apiKey, api_secret: apiSecret },
  );

export const createFivetranIntegration = (
  envId: string,
  apiKey: string,
  apiSecret: string,
  destinationGroupIds: string[],
): Promise<CreateFivetranIntegrationResponse> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/etl-tool/fivetran`,
    {
      api_key: apiKey,
      api_secret: apiSecret,
      destination_group_ids: destinationGroupIds,
    },
  );

export const listFivetranDestinationGroups = (
  envId: string,
  integrationId: string,
): Promise<FivetranDestinationGroups> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/etl-tool/fivetran/${integrationId}/list-destination-groups`,
  );

export const patchFivetranIntegration = (
  envId: string,
  integrationId: string,
  destinationGroupIds: string[],
  apiKey?: string,
  apiSecret?: string,
): Promise<PatchFivetranIntegrationResponse> =>
  patch(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/etl-tool/fivetran/${integrationId}`,
    {
      destination_group_ids: destinationGroupIds,
      ...(apiKey && { api_key: apiKey }),
      ...(apiSecret && { api_secret: apiSecret }),
    },
  );

export const fetchPreDeleteInfo = (
  integrationId: string,
): Promise<PreDeleteResponse> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/${integrationId}/pre-delete`,
  );

export const disconnectIntegration = (integrationId: string): Promise<void> =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/${integrationId}`,
  );

export const fetchIntegrationRuleCounts = (
  environmentId: string,
): Promise<Record<string, number>> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/account/integrations/rule-counts/${environmentId}`,
  );
